import { useEffect, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import api from "../../../api";
import { addToPanelBasket, panelBasketHas } from "../../../utils/panelBasket";

/**
 * "All Mock Exams" / "All Past Papers" pack offer at the top of the matching page.
 * Hidden when a plan already includes everything or the pack is already owned.
 *
 * @param {number} resourceType mirrors config('constants.product_types'): 2 mock exams, 3 past papers
 * @param {number} [refreshKey] bump after a panel purchase so the banner hides once the pack is owned
 */
export default function PanelPackOffer({ resourceType, refreshKey = 0 }) {
  const [pack, setPack] = useState(null);
  const [inBasket, setInBasket] = useState(false);

  // The bar and this banner both write the same store, so follow it rather than
  // local state alone.
  useEffect(() => {
    const sync = () => setInBasket(pack ? panelBasketHas("bundle", pack.id) : false);
    sync();
    window.addEventListener("panelBasketUpdated", sync);
    return () => window.removeEventListener("panelBasketUpdated", sync);
  }, [pack]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/bundles")
      .then((res) => {
        if (cancelled) return;
        // Individual papers/mocks already bought must not hide this — already_owned
        // is only the pack itself. After the pack is paid, this offer disappears.
        const match = (res.data?.data || []).find(
          (b) => Number(b.resource_type) === Number(resourceType) && !b.already_owned && !b.covered_by_plan
        );
        setPack(match || null);
      })
      .catch(() => setPack(null));
    return () => {
      cancelled = true;
    };
  }, [resourceType, refreshKey]);

  if (!pack) return null;

  // Into the same basket as the rows below, so a pack and a few single papers are
  // one payment and one parent confirmation, not two separate dialogs on one page.
  const buy = () => {
    addToPanelBasket({
      resourceType: "bundle",
      resourceId: pack.id,
      name: pack.name,
      price: Number(String(pack.price ?? "").replace(/[^0-9.]/g, "")) || 0,
      priceDisplay: pack.price_display || null,
    });
  };

  return (
    <>
    <Alert
      icon={false}
      severity="info"
      sx={{
        mb: 3,
        borderRadius: 2,
        bgcolor: "#f5f3ff",
        border: "1px solid #ddd6fe",
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: "#4c1d95" }}>
            {String(pack.name || "").replace(/\bbundle\b/gi, "Pack")} — {pack.price_display}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Unlock all {pack.item_count} with one payment
            {pack.access_months ? `, for ${pack.access_months} months` : ", forever"}.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={buy}
          disabled={inBasket}
          sx={{ flexShrink: 0, textTransform: "none", fontWeight: 700, bgcolor: "#7c3aed", "&:hover": { bgcolor: "#6d28d9" } }}
        >
          {inBasket ? "In basket" : "Get the pack"}
        </Button>
      </Stack>
    </Alert>
    </>
  );
}
